import { Button, Text } from "@athena/ui";
import { Separator } from "@athena/ui/components/separator";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import logo from "../assets/logo.png";
import logoWhite from "../assets/logo-white.png";

/**
 * What Auth0 said, when it refused.
 *
 * A post-login Action that calls `api.access.deny` sends the browser back here with
 * `error=access_denied` and a description — and nothing read either, so a refusal was
 * indistinguishable from arriving at the login page normally. An allowlist Action denied a pen
 * tester with "Admin access denied." for three days while every screen showed only this form and
 * a button; the message existed the whole time, in a query string nobody looked at.
 *
 * `auth0-react` parses that callback and exposes it as `error`, so there is no URL to read.
 * `error_description` is the part written for a person; `error` is the OAuth code behind it.
 */
const describeAuthError = (error: Error): string => {
    const oauth = error as Error & { error?: string; error_description?: string };
    return oauth.error_description || error.message || oauth.error || "Sign-in failed.";
};

export function LoginPage() {
    const { loginWithRedirect, isAuthenticated, error } = useAuth0();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? "/";

    const handleLogin = async () => {
        await loginWithRedirect();
    };

    // Not while there is a refusal to show: Auth0 can consider a session authenticated *and* have
    // denied this application, and forwarding on would hide the reason and bounce the user back.
    useEffect(() => {
        if (isAuthenticated && !error) navigate(returnTo, { replace: true });
    }, [
        isAuthenticated,
        error,
        navigate,
        returnTo,
    ]);

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
            <div className="bg-muted relative hidden flex-col justify-between p-10 lg:flex">
                <div className="flex items-center gap-2 text-lg font-semibold">
                    <img src={logo} alt="LOGO" className="dark:hidden" style={{ height: 180, width: 180 }} />
                    <img src={logoWhite} alt="LOGO" className="hidden dark:block" style={{ height: 180, width: 120 }} />
                </div>
            </div>

            <div className="flex flex-col">
                <div className="flex flex-1 items-center justify-center px-8">
                    <div className="w-full max-w-sm space-y-6">
                        {/* The logo panel is desktop-only, so on a phone the logo sits above the heading. */}
                        <div className="flex justify-center lg:hidden">
                            <img src={logo} alt="LOGO" className="dark:hidden" style={{ height: 120, width: 120 }} />
                            <img
                                src={logoWhite}
                                alt="LOGO"
                                className="hidden dark:block"
                                style={{ height: 120, width: 80 }}
                            />
                        </div>
                        <div className="space-y-2 text-center">
                            <h1 className="text-3xl font-bold tracking-tight">ADMIN PORTAL</h1>
                            <p className="text-muted-foreground">
                                Login in to manage partner organizations, users, courses and more.
                            </p>
                        </div>

                        {error ? (
                            <div
                                role="alert"
                                className="border-destructive/40 bg-destructive/10 flex items-start gap-2 rounded-md border p-3"
                            >
                                <ShieldAlert className="text-destructive mt-0.5 h-4 w-4 shrink-0" />
                                <div className="space-y-1">
                                    <Text weight="medium" className="text-sm">
                                        Unable to sign you in
                                    </Text>
                                    <Text className="text-muted-foreground text-sm">{describeAuthError(error)}</Text>
                                </div>
                            </div>
                        ) : null}

                        <div className="flex justify-center">
                            <Button onClick={handleLogin}>{error ? "Try again" : "Let's Go"}</Button>
                        </div>
                        <Separator />
                    </div>
                </div>
            </div>
        </div>
    );
}
